import React from 'react';
import { Button } from "@/components/ui/button";
import { Brain, Trash2, FileText } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { GroundingToolsMenu } from "../GroundingToolsMenu";

interface ChatHeaderProps {
  selectedMode: string;
  messagesLength: number;
  conversationId: string | null;
  onModeChange?: (mode: string) => void;
  onGenerateSummary: () => void;
  onDeleteConversation: () => void;
}

const MODE_NAMES: Record<string, string> = {
  general: 'General Mode',
  cbt: 'CBT Mode',
  psychotherapy: 'Reflective Depth Mode',
  qsp: 'QuantumSelf Psychology™ Mode'
};

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  selectedMode,
  messagesLength,
  conversationId,
  onModeChange,
  onGenerateSummary,
  onDeleteConversation
}) => {
  return (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mx-4 sm:mx-6 mt-4 sm:mt-6 mb-3 sm:mb-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="bg-primary/10 text-primary px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium flex items-center gap-1.5 sm:gap-2">
          <Brain className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          Mode: {MODE_NAMES[selectedMode] || 'General Mode'}
        </div>
      </div>
      
      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
        <GroundingToolsMenu />
        {messagesLength > 4 && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={onGenerateSummary}
            className="gap-1.5 h-8 text-xs sm:text-sm px-2 sm:px-3"
          >
            <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Session Summary</span>
            <span className="sm:hidden">Summary</span>
          </Button>
        )}
        {onModeChange && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onModeChange(selectedMode)}
            className="h-8 text-xs sm:text-sm px-2 sm:px-3"
          >
            <span className="hidden sm:inline">Change Mode</span>
            <span className="sm:hidden">Mode</span>
          </Button>
        )}
        {conversationId && messagesLength > 1 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs sm:text-sm px-2 sm:px-3 text-destructive border-destructive/30 hover:bg-destructive/10">
                <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Delete Conversation</span>
                <span className="sm:hidden">Delete</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="mx-4 max-w-[calc(100vw-2rem)]">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-base sm:text-lg">Delete entire conversation?</AlertDialogTitle>
                <AlertDialogDescription className="text-sm">
                  This will permanently delete all messages in this conversation. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onDeleteConversation} className="w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
};
